import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import queryString from 'query-string';

import GeneralSections from 'components/admin/CreateAdminStepOneGeneralSection';
import PersonalInfoSections from 'components/admin/CreateAdminStepOnePersonalInfoSection';
import StatusSections from 'components/admin/CreateAdminStepOneStatusSection';
import PermissionSections from 'components/admin/CreateAminStepOnePermissionSection';
import { SaveAndBackButtons } from 'components/base/BottomStepComponent';
import ContentSections from 'components/base/ContentSections';
import DeletePrompt from 'components/base/DeletePrompt';
import BasePrompt from 'components/base/prompt/BasePrompt';
import { DELETE_RELATED_SECTIONS, SavedStatus } from 'config/CustomEnums';
import { ADMINISTRATOR_TYPES } from 'config/CustomEnums';
import { PermissionCodes } from 'config/PermissionCodes';

import { createAction } from 'utils';

const adminPermission = [
  PermissionCodes.changeAdministrator,
  PermissionCodes.changeAdministratorGroup,
];
function CreateAdminStepOne({ saved, admin }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const params = useParams();
  const { id } = params;
  const { watch, getValues, trigger } = useFormContext();
  const watchSuperAdmin = watch('isSuperuser');

  const watchPermissionGroups = watch('groups');
  const watchPermissions = watch('permissions');

  const search = queryString.parse(history.location.search);
  const watchAdminType = watch('adminType');
  const adminType = useMemo(() => {
    if (id) {
      return watchAdminType;
    }
    const type = search?.type;
    if (type === '0') {
      return ADMINISTRATOR_TYPES.CMS_ADMINISTRATOR;
    } else if (type === '1') {
      return ADMINISTRATOR_TYPES.MERCHANT_APP_ACCOUNT;
    }
    return ADMINISTRATOR_TYPES.CMS_ADMINISTRATOR;
  }, [id, watchAdminType, search?.type]);

  const [saveAlertConfig, setSaveAlertConfig] = useState(null);
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);

  const permissions = watchPermissions;
  const groups = watchPermissionGroups;
  const patchData = () => {
    const data = getValues();
    data.adminType = adminType;
    if (!id) {
      delete data.pk;
    }
    if (data.adminType === ADMINISTRATOR_TYPES.MERCHANT_APP_ACCOUNT) {
      delete data.isSuperuser;
    } else {
      delete data.merchantStores;
    }
    dispatch({
      type: 'admin/updateOrCreate',
      payload: { data },
    });
  };

  const sections = [
    <GeneralSections adminType={adminType} />,
    <PersonalInfoSections adminType={adminType} />,
    <StatusSections adminType={adminType} />,
  ];

  if (!watchSuperAdmin) {
    sections.push(<PermissionSections adminType={adminType} />);
  }
  return (
    <>
      <ContentSections sections={sections} hidePreview={true} />
      <SaveAndBackButtons
        saveTempText={id ? 'Delete' : null}
        saveText={id ? 'Update' : 'Save'}
        backAction={() => {
          history.push('/administrators');
        }}
        saveAction={async () => {
          if (saved === SavedStatus.saving) {
            return;
          }
          const triggerFields = [
            'name',
            'adminType',
            'email',
            'password',
            'confirmPassword',
            'merchantStores'
          ];
          // const [password, confirmPassword] = getValues(['password', 'confirmPassword']);
          const isValid = await trigger(triggerFields);
          if (!isValid) {
            return;
          }

          if (watchSuperAdmin) {
            setSaveAlertConfig({
              title: 'Confirm to give this admin permission',
              content:
                'As this a superadmin, then the admin will gain full system permission by doing so. ',
            });
            return;
          }
          console.log('groups:', groups);
          const selectedAdminPermissionGroups = groups?.filter(
            (group) =>
              (group.permissions?.edges || group.permissions || []).filter(
                (val) =>
                  adminPermission.includes(
                    val?.node?.codename || val?.codename,
                  ),
              ).length > 0,
          );

          if (selectedAdminPermissionGroups?.length > 0) {
            setSaveAlertConfig({
              title: 'Confirm to give this admin permission',
              content: `As this admin belong to ${selectedAdminPermissionGroups
                .map((group) => group.name)
                .join(
                  ',',
                )}, then the admin will gain full system permission by doing so. `,
            });
            return;
          }

          const selectedAdminPermissions = permissions?.filter((permission) =>
            adminPermission.includes(permission.codename),
          );
          if (selectedAdminPermissions?.length > 0) {
            setSaveAlertConfig({
              title: 'Confirm to give this admin permission',
              content: `As "change admin permissions" is set, then the admin will gain full system permission by doing so. `,
            });
            return;
          }
          patchData();
        }}
        saveTempAction={() => setShowDeletePrompt(true)}
        tempRequires={PermissionCodes.deleteAdministrator}
      />
      <DeletePrompt
        message={{
          title: 'Delete this Admin',
          content: `Are you sure you want to delete the administrator "${admin.name}"? All of the following related items will be deleted:`,
        }}
        title={'Admin'}
        data={admin}
        relatedSections={DELETE_RELATED_SECTIONS.ADMIN}
        show={showDeletePrompt}
        onClose={() => {
          setShowDeletePrompt(false);
        }}
        onConfirm={() => {
          setShowDeletePrompt(false);
          dispatch({
            type: `admin/updateState`,
            payload: { checkedList: [admin], formChanged: false },
          });
          dispatch(
            createAction('admin/delete')({
              afterAction: () => {
                history.push('/administrators');
              },
            }),
          );
        }}
      />
      <BasePrompt
        show={!!saveAlertConfig}
        closeAction={() => setSaveAlertConfig(null)}
        rightButton={{
          text: 'Confirm',
          action: () => {
            setSaveAlertConfig(null);
            patchData();
          },
        }}
        title={saveAlertConfig?.title}
        description={saveAlertConfig?.content}
      />
    </>
  );
}

const mapPropsToState = (state) => ({
  admin: state.admin.oneAdmin || {},
  saved: state.admin.saved,
});

export default connect(mapPropsToState)(CreateAdminStepOne);
